import React, { useContext } from "react";

import {
  DataTable,
  HeaderType,
  ItemAction,
  Pill,
} from "@validereinc/common-components";

import { EquipmentType, EventAdapter } from "@validereinc/domain";

import useLocalization from "#src/hooks/useLocalization";

import { linkToEquipmentDetail, linkToFacilities } from "#src/Routers/links";

import { EventContext } from "#src/contexts/EventContext";

import { toTitleCaseString } from "@validereinc/utilities";
import { RoutingLink } from "../RoutingLink";

export const EventEquipmentTable = () => {
  const { event, isLoading, onRefetchEvent } = useContext(EventContext) ?? {};
  const { localize } = useLocalization();

  const headers: Array<HeaderType<EquipmentType>> = [
    {
      label: `${localize("Equipment")} Name`,
      key: "name",
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToEquipmentDetail(item.id)}>
          {item.name}
        </RoutingLink>
      ),
    },
    {
      label: `${localize("Equipment")} Type`,
      key: "type_id",
      renderComponent: ({ item }) => item?.type?.name ?? "-",
    },
    {
      label: `${localize("Facility")} Name`,
      key: "facility_name",
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToFacilities(item.facility_id)}>
          {item?.facility?.name ?? "-"}
        </RoutingLink>
      ),
    },
    {
      key: "status",
      label: "Status",
      renderComponent: ({ item }) => (
        <Pill variant={item.status === "active" ? "success" : "error"}>
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Effective Date",
      key: "effective_date",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.effective_date}
          withTime
        />
      ),
    },
  ];

  const onRemove = async ({ equipmentId }: { equipmentId: string }) => {
    if (!event?.id) return;

    await EventAdapter.assets.deleteOne({
      assetId: equipmentId,
      eventId: event?.id,
    });

    onRefetchEvent?.();
  };

  return (
    <DataTable
      headers={headers}
      items={event?.equipment?.data ?? []}
      getItemId={(item) => item.id}
      isLoading={isLoading}
      getItemActions={({ item: { id } }): ItemAction[] => [
        {
          label: "Remove",
          buttonProps: {
            onClick: () => onRemove({ equipmentId: id }),
            icon: "trash",
          },
        },
      ]}
    />
  );
};
